import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAllBoards } from '../services/api';
import { isMockMode } from '../services/api';
import { runTrelloSync } from '../services/trelloSync';
import { hasSyncData, isSyncStale } from '../services/syncStore';
import { MOCK_BOARDS } from '../services/mockTrello';
import { trelloWS } from '../services/trelloWebSocket';
import { getToken, apiFetch } from '../services/apiClient';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // Read the saved worker synchronously so a hard refresh on a deep route
  // (e.g. /sup/project/123) doesn't briefly see no-worker and bounce to the
  // login/dashboard before the effect runs.
  const [worker, setWorker]   = useState(() => {
    try { const s = localStorage.getItem('te_worker'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [boards, setBoards]   = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle'); // idle | syncing | done | error

  useEffect(() => {
    const savedWorker = localStorage.getItem('te_worker');
    if (savedWorker) { try { setWorker(JSON.parse(savedWorker)); } catch {} }
    loadBoards();
    refreshRole();
  }, []);

  // The role is saved at sign-in, and every page reads it from there — so someone made an
  // Executive later kept the old pages on a computer that stayed signed in (Ryan, Oct 18 2026:
  // Erin got the Executive podcast on her phone, not her desktop). On open, ask the server who
  // this is now; if the role changed, save it and reload once so every page sees it.
  async function refreshRole() {
    let s = null;
    try { s = JSON.parse(localStorage.getItem('titan_session') || 'null'); } catch { return; }
    if (!s || !s.token || s.role === 'admin') return;
    try {
      const me = await apiFetch('/me');
      if (!me || !me.role || me.role === s.role) return;
      // "View as" answers for someone else — never save their role as ours.
      if (s.user && s.user.id && String(me.id) !== String(s.user.id)) return;
      localStorage.setItem('titan_session', JSON.stringify({ ...s, role: me.role, user: { ...(s.user || {}), role: me.role } }));
      try {
        const w = JSON.parse(localStorage.getItem('te_worker') || 'null');
        if (w) localStorage.setItem('te_worker', JSON.stringify({ ...w, role: me.role }));
      } catch { /* nothing saved */ }
      window.location.reload();
    } catch { /* offline or signed out — leave it as it is */ }
  }

  async function loadBoards() {
    try {
      if (isMockMode()) {
        setBoards(MOCK_BOARDS);
      } else {
        const b = await getAllBoards();
        setBoards(b);
      }
    } catch (e) {
      setError(e.message || 'Could not connect to Trello.');
    } finally {
      setLoading(false);
    }
  }

  // Called once at admin login — runs full sync and connects WebSocket
  async function runInitialSync(onProgress) {
    if (isMockMode()) return;
    // Connect WebSocket for real-time updates — the server now checks the app's own
    // session token on the socket, not the Trello token.
    trelloWS.connect(getToken());
    if (hasSyncData() && !isSyncStale()) {
      setSyncStatus('done');
      return;
    }
    setSyncStatus('syncing');
    try {
      await runTrelloSync(onProgress, { force: false });
      setSyncStatus('done');
    } catch (e) {
      setSyncStatus('error');
      console.error('Sync failed:', e);
    }
  }

  function loginAdmin() {
    localStorage.setItem('te_admin_authed', 'true');
  }

  function login(member) {
    setWorker(member);
    localStorage.setItem('te_worker', JSON.stringify(member));
  }

  function loginStaff(user) {
    // Staff users are stored in the same 'te_worker' slot so the app
    // knows a user is present, but routing is done by role from the session.
    setWorker(user);
    localStorage.setItem('te_worker', JSON.stringify(user));
  }

  function logout() {
    setWorker(null);
    setMembers([]);
    trelloWS.disconnect();
    localStorage.removeItem('te_worker');
    localStorage.removeItem('te_admin_authed');
    localStorage.removeItem('titan_session');
  }

  return (
    <AuthContext.Provider value={{
      worker, boards, members, loading, error,
      syncStatus, runInitialSync,
      loginAdmin, loginStaff, login, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
